import { useState } from "react";
import { Sparkles, PenLine, CalendarDays, LogOut, MessagesSquare, Waypoints, Repeat, CreditCard, Menu, X, MonitorPlay, ReceiptText } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import IdentitasKreator from "@/features/IdentitasKreator";
import Brainstorming from "@/features/Brainstorming";
import SusunScript from "@/features/SusunScript";
import PetaKonversi from "@/features/PetaKonversi";
import RecreateKonten from "@/features/RecreateKonten";
import KalenderKonten from "@/features/KalenderKonten";
import KelolaLangganan from "@/features/KelolaLangganan";
import Teleprompter from "@/features/Teleprompter";
import RateCardGenerator from "@/features/RateCardGenerator";

const NAV = [
  { key: "identitas", label: "Identitas Kreator", icon: Sparkles },
  { key: "brainstorming", label: "Brainstorming", icon: MessagesSquare },
  { key: "script", label: "Susun Script", icon: PenLine },
  { key: "teleprompter", label: "Teleprompter", icon: MonitorPlay },
  { key: "funnel", label: "Peta Konversi", icon: Waypoints },
  { key: "recreate", label: "Recreate Konten", icon: Repeat },
  { key: "kalender", label: "Kalender Konten", icon: CalendarDays },
  { key: "ratecard", label: "Rate Card Generator", icon: ReceiptText },
  { key: "langganan", label: "Kelola Langganan", icon: CreditCard },
];

export default function Dashboard() {
  const { user, logout } = useAuth();
  const [active, setActive] = useState("identitas");
  const [pendingScript, setPendingScript] = useState(null);
  const [pendingTeleprompter, setPendingTeleprompter] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const sendToCalendar = (text) => {
    setPendingScript(text);
    setActive("kalender");
  };

  const sendToTeleprompter = (text) => {
    setPendingTeleprompter(text);
    setActive("teleprompter");
  };

  const activeLabel = NAV.find((n) => n.key === active)?.label || "";

  const NavButtons = ({ prefix, onSelect }) => (
    <nav className="flex flex-col gap-1.5 flex-1">
      {NAV.map((item) => {
        const isActive = active === item.key;
        return (
          <button
            key={item.key}
            data-testid={`${prefix}${item.key}`}
            onClick={() => { setActive(item.key); onSelect?.(); }}
            className={`flex items-center gap-3 rounded-xl px-4 py-3 text-sm font-semibold transition-colors duration-200 ${
              isActive ? "bg-sk-brand/15 text-sk-brand" : "text-sk-muted hover:text-sk-cream hover:bg-sk-bg"
            }`}
          >
            <item.icon className={`h-5 w-5 ${isActive ? "text-sk-brand" : ""}`} />
            {item.label}
          </button>
        );
      })}
    </nav>
  );

  const UserBlock = ({ logoutTestId = "logout-btn" }) => (
    <div className="border-t border-sk-line pt-4 mt-4">
      <div className="flex items-center gap-3 px-2 mb-3">
        {user?.picture ? (
          <img src={user.picture} alt="" className="h-9 w-9 rounded-full object-cover border border-sk-line" />
        ) : (
          <div className="h-9 w-9 rounded-full bg-sk-brand/20 flex items-center justify-center text-sk-brand font-bold">
            {user?.name?.[0]?.toUpperCase() || "K"}
          </div>
        )}
        <div className="min-w-0">
          <p className="text-sm font-semibold text-sk-cream truncate">{user?.name || "Kreator"}</p>
          <p className="text-xs text-sk-muted truncate">{user?.email}</p>
        </div>
      </div>
      {user?.subscription_expires_at && (
        <p className="text-[11px] text-sk-muted px-2 mb-2">Langganan aktif s/d {new Date(user.subscription_expires_at).toLocaleDateString("id-ID", { day: "numeric", month: "short", year: "numeric" })}</p>
      )}
      <button
        onClick={logout}
        data-testid={logoutTestId}
        className="w-full flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-medium text-sk-muted hover:text-sk-cream hover:bg-sk-bg transition-colors"
      >
        <LogOut className="h-4 w-4" /> Keluar
      </button>
    </div>
  );

  return (
    <div className="min-h-screen bg-sk-bg text-sk-cream flex">
      {/* Desktop sidebar */}
      <aside className="hidden md:flex flex-col w-64 shrink-0 bg-sk-surface border-r border-sk-line px-4 py-6 sticky top-0 h-screen">
        <img src="/logo.png" alt="Studio Kreator" className="h-9 w-auto self-start mb-10 ml-2" data-testid="sidebar-logo" />
        <NavButtons prefix="nav-" />
        <UserBlock />
      </aside>

      {/* Mobile drawer */}
      <div className={`md:hidden fixed inset-0 z-40 ${drawerOpen ? "" : "pointer-events-none"}`} aria-hidden={!drawerOpen}>
        <div
          onClick={() => setDrawerOpen(false)}
          className={`absolute inset-0 bg-black/40 transition-opacity duration-300 ${drawerOpen ? "opacity-100" : "opacity-0"}`}
          data-testid="drawer-backdrop"
        />
        <aside className={`absolute left-0 top-0 h-full w-[17rem] max-w-[82%] bg-sk-surface border-r border-sk-line px-4 py-6 flex flex-col shadow-2xl transition-transform duration-300 ${drawerOpen ? "translate-x-0" : "-translate-x-full"}`}>
          <div className="flex items-center justify-between mb-8">
            <img src="/logo.png" alt="Studio Kreator" className="h-8 w-auto ml-1" />
            <button onClick={() => setDrawerOpen(false)} data-testid="drawer-close" className="p-2 text-sk-muted hover:text-sk-cream">
              <X className="h-5 w-5" />
            </button>
          </div>
          <NavButtons prefix="nav-mobile-" onSelect={() => setDrawerOpen(false)} />
          <UserBlock logoutTestId="logout-btn-mobile" />
        </aside>
      </div>

      {/* Main */}
      <main className="flex-1 min-w-0">
        {/* Mobile top bar — minimal */}
        <div className="md:hidden sticky top-0 z-20 flex items-center gap-3 bg-sk-bg/90 backdrop-blur-md border-b border-sk-line px-3 py-3">
          <button onClick={() => setDrawerOpen(true)} data-testid="nav-hamburger" className="p-2 -ml-1 text-sk-cream hover:text-sk-brand transition-colors">
            <Menu className="h-6 w-6" />
          </button>
          <span className="font-bold text-sk-cream truncate">{activeLabel}</span>
          <span className="ml-auto">
            {user?.picture ? (
              <img src={user.picture} alt="" onClick={() => setDrawerOpen(true)} className="h-8 w-8 rounded-full object-cover border border-sk-line" />
            ) : (
              <button onClick={() => setDrawerOpen(true)} className="h-8 w-8 rounded-full bg-sk-brand/20 flex items-center justify-center text-sk-brand font-bold text-sm">
                {user?.name?.[0]?.toUpperCase() || "K"}
              </button>
            )}
          </span>
        </div>

        <div className="max-w-5xl mx-auto px-5 sm:px-6 lg:px-10 py-8 sm:py-10 pb-16">
          {active === "identitas" && <IdentitasKreator />}
          {active === "brainstorming" && <Brainstorming />}
          {active === "script" && <SusunScript onSendToCalendar={sendToCalendar} onSendToTeleprompter={sendToTeleprompter} />}
          {active === "teleprompter" && (
            <Teleprompter pendingScript={pendingTeleprompter} clearPending={() => setPendingTeleprompter(null)} />
          )}
          {active === "funnel" && <PetaKonversi onSendToCalendar={sendToCalendar} />}
          {active === "recreate" && <RecreateKonten onSendToCalendar={sendToCalendar} />}
          {active === "kalender" && (
            <KalenderKonten pendingScript={pendingScript} clearPending={() => setPendingScript(null)} />
          )}
          {active === "langganan" && <KelolaLangganan />}
          {active === "ratecard" && <RateCardGenerator />}
        </div>
      </main>
    </div>
  );
}
